export interface Quota {
  plan: "anonymous" | "free" | "paid" | "superuser";
  used: number;
  totalUsed: number;
  limit: number | null;
  remaining: number | null;
  resetsAt: string | null;
}

export class ClientApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function parseError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  throw new ClientApiError(
    body?.error?.code ?? "REQUEST_FAILED",
    body?.error?.message ?? "The request failed. Please try again.",
    response.status,
  );
}

export async function fetchQuota(token: string | null): Promise<Quota> {
  const response = await fetch("/api/quota", {
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    credentials: "same-origin",
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<Quota>;
}

export async function removeBackground(file: File, token: string | null, turnstileToken = ""): Promise<{ blob: Blob; quota: Quota }> {
  const form = new FormData();
  form.set("image", file);
  form.set("turnstileToken", turnstileToken);
  const response = await fetch("/api/remove", {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) return parseError(response);
  const rawLimit = response.headers.get("x-quota-limit") ?? "1";
  const rawRemaining = response.headers.get("x-quota-remaining") ?? "0";
  const limit = rawLimit === "unlimited" ? null : Number(rawLimit);
  const remaining = rawRemaining === "unlimited" ? null : Number(rawRemaining);
  return {
    blob: await response.blob(),
    quota: {
      plan: (response.headers.get("x-quota-plan") as Quota["plan"] | null) ?? (token ? "free" : "anonymous"),
      used: limit === null || remaining === null ? 0 : limit - remaining,
      totalUsed: Number(response.headers.get("x-quota-total-used") ?? (limit === null || remaining === null ? 0 : limit - remaining)),
      limit,
      remaining,
      resetsAt: response.headers.get("x-quota-reset") === "never" ? null : response.headers.get("x-quota-reset"),
    },
  };
}

export interface CreditRequest {
  uid: string;
  xHandle: string;
  status: "pending" | "approved" | "declined";
  createdAt: string;
  updatedAt: string;
}

function authHeaders(token: string): HeadersInit {
  return { authorization: `Bearer ${token}` };
}

export async function fetchCreditRequest(token: string): Promise<CreditRequest | null> {
  const response = await fetch("/api/credit-request", { headers: authHeaders(token), credentials: "same-origin" });
  if (!response.ok) return parseError(response);
  return ((await response.json()) as { request: CreditRequest | null }).request;
}

export async function submitCreditRequest(token: string, xHandle: string): Promise<CreditRequest> {
  const response = await fetch("/api/credit-request", {
    method: "POST",
    headers: { ...authHeaders(token), "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ xHandle }),
  });
  if (!response.ok) return parseError(response);
  return ((await response.json()) as { request: CreditRequest }).request;
}

export async function fetchPendingCreditRequests(token: string): Promise<CreditRequest[]> {
  const response = await fetch("/api/admin/credit-requests", { headers: authHeaders(token), credentials: "same-origin" });
  if (!response.ok) return parseError(response);
  return ((await response.json()) as { requests: CreditRequest[] }).requests;
}

export async function approveCreditRequest(token: string, uid: string): Promise<void> {
  const response = await fetch(`/api/admin/credit-requests/${encodeURIComponent(uid)}/approve`, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "same-origin",
  });
  if (!response.ok) return parseError(response);
}
